'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '../image/viewport-image';
import Link from '@eliadmin/core/src/components/link';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../shared/heading';

import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export function Categories({ categories }: { categories: CategoriesEntity[] }) {
  const { t } = useTranslations();

  return (
    <CarouselProvider>
      <section
        className={cn(
          'custom-section',
          'bg-[#F7F7F2] !pr-0 pb-12 pt-10 sm:pb-20 sm:pt-16 lg:pb-24 lg:pt-20',
        )}
      >
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12',
          )}
        >
          <div className="flex w-full flex-col items-start justify-start gap-6 pr-6 sm:gap-8 md:flex-row md:items-end md:justify-between lg:gap-16">
            <Heading subtitle={t('text176')} title={t('text2317')} />
            <p className="w-full text-sm font-light text-black/70 sm:text-base md:max-w-[397px]">
              {t('text2318')}
            </p>
          </div>
          <Carousel
            categoriesFooter
            className={{
              container: 'w-full gap-4 sm:gap-5 lg:gap-6',
              wrapper: 'w-full',
              main: 'w-full pb-10 sm:pb-24',
              slide: 'w-full max-w-[250px] sm:max-w-[265px] md:max-w-[280px] lg:max-w-[292px]',
            }}
            slides={categories.slice(0, 8).map(category => (
              <CategoryCard category={category} key={category.id} />
            ))}
          />
          <div className="block w-full pr-6 sm:hidden">
            <Button asChild className="w-full whitespace-nowrap">
              <Link prefetchOnHover href={ROUTES.CATEGORIES()}>
                {t('text2319')}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </CarouselProvider>
  );
}

function CategoryCard({ category }: { category: CategoriesEntity }) {
  const { t } = useTranslations();

  return (
    <div
      className={cn(
        'group',
        'relative flex h-[292px] w-full items-end justify-center overflow-hidden rounded-[16px] p-4 sm:h-[320px] lg:h-[340px]',
      )}
    >
      <div
        className={cn(
          'absolute left-0 top-0 z-[5] flex h-full w-full translate-y-full flex-col items-center justify-end gap-3 bg-primary p-4 sm:gap-4 lg:gap-6',
          'transition-all group-hover:translate-y-0',
        )}
      >
        <div className="flex w-full flex-col items-center justify-center gap-3">
          <strong className="text-center text-lg font-medium tracking-tight text-white sm:text-xl lg:text-2xl">
            {category.name}
          </strong>
          {/* <div className="flex w-full flex-col items-center justify-center gap-2.5">
            <HtmlDescription
              color="white"
              content={category?.short_description ?? ''}
              size="sm"
              className={cn(
                'line-clamp-2 w-full text-center',
                'ea-category-card__short-description',
              )}
            />
          </div> */}
        </div>
        <Button asChild variant={'secondary'} className="w-full">
          <Link prefetchOnHover href={ROUTES.CATEGORY({ slug: category.slug })}>
            {t('text2299')}
          </Link>
        </Button>
      </div>
      <h3
        data-ea-visual-tag="h5"
        className="relative z-[2] text-center text-lg font-medium text-white sm:text-xl lg:text-2xl"
      >
        {category.name}
      </h3>
      <div
        className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-category-card__image-overlay')}
      />
      <Image
        src={category?.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(category?.image, category?.name)}
        width={292}
        height={340}
        quality={75}
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        sizes="(min-width: 1024px) 292px, (min-width: 768px) 280px, (min-width: 640px) 265px, 250px"
      />
    </div>
  );
}
